import {isFeatureEnabled} from '@github-ui/feature-flags'
import {DEFAULT_MAXIMUM_ASSIGNEES} from '@github-ui/item-picker-assignee'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import useIsMounted from '@github-ui/use-is-mounted'
import {SelectPanel, type SelectPanelGroupedListProps as GroupedListProps, Spinner} from '@primer/react'
import {memo, useCallback, useEffect, useRef, useState} from 'react'

import type {SupportedSuggestionOptions} from '../../../../helpers/suggestions'
import {useAssigneeLimitReached} from '../../../../hooks/editors/use-assignee-limit'
import {useSelectPanel, type UseSelectPanelProps} from '../../../../hooks/editors/use-select-panel'
import {useApiRequest} from '../../../../hooks/use-api-request'
import {FieldValue} from './core'

type SidebarSelectPanelProps<T extends SupportedSuggestionOptions> = {
  placeholderText: string
  groupMetadata?: GroupedListProps['groupMetadata']
  displayValue: React.ReactNode
  maximumAssignees?: number
} & Omit<UseSelectPanelProps<T>, 'open' | 'onOpenChange' | 'renderAnchor' | 'overlayProps'>

function SidebarSelectPanelFunc<T extends SupportedSuggestionOptions>({
  displayValue,
  saveSelected,
  selected,
  columnId,
  maximumAssignees = DEFAULT_MAXIMUM_ASSIGNEES,
  ...useSelectPanelProps
}: SidebarSelectPanelProps<T>) {
  const [open, setOpen] = useState(false)
  const onOpenChange = useCallback((next: boolean) => setOpen(next), [])

  const [loading, setLoading] = useState(false)
  const requestFinishedRef = useRef(true)
  const isMounted = useIsMounted()

  const {perform: handleSave} = useApiRequest({
    request: async (_selected: Array<T>) => {
      setLoading(true)
      requestFinishedRef.current = false
      await saveSelected(_selected)
      requestFinishedRef.current = true
      setLoading(false)
    },
    rollback: () => {
      requestFinishedRef.current = true
      if (isMounted()) setLoading(false)
    },
  })

  // This is ugly, but we can't just setLoading(false) inside of request because we have to
  // wait until we get the updated value. Otherwise it will flash with the old value first.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (requestFinishedRef.current) setLoading(false)
  }, [selected])

  const selectPanelProps = useSelectPanel({
    ...useSelectPanelProps,
    open,
    onOpenChange,
    selected,
    columnId,
    renderAnchor: props => (
      <FieldValue interactable as="button" {...props}>
        {loading ? <Spinner size="small" /> : displayValue}
      </FieldValue>
    ),
    saveSelected: handleSave,
    maximumAssignees,
  })

  const selectedCount = selectPanelProps.selected?.length ?? 0
  const isAssigneeLimitReached = useAssigneeLimitReached(columnId, selectedCount, maximumAssignees)
  const isFeatureFlagEnabled = isFeatureEnabled('projects_assignee_max_limit')

  return (
    <SelectPanel
      {...selectPanelProps}
      notice={
        isFeatureFlagEnabled && isAssigneeLimitReached
          ? {
              text: LABELS.limitWarning(maximumAssignees),
              variant: 'warning',
            }
          : undefined
      }
      overlayProps={{
        width: 'small',
        height: 'auto',
        maxHeight: 'large',
        ...selectPanelProps.overlayProps,
      }}
    />
  )
}

SidebarSelectPanelFunc.displayName = 'SidebarSelectPanelFunc'

export const SidebarSelectPanel = memo(SidebarSelectPanelFunc) as typeof SidebarSelectPanelFunc
